import React, { useState, useEffect, useCallback } from "react";
import api, { formatVND, normalizePhone, blockWhitespaceKey } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Plus, Trash2, Users, DollarSign } from "lucide-react";
import DateInputVN from "@/components/DateInputVN";

const PRESET_TAGS = ["VIP", "Khách quen", "Thuê tháng", "Nhóm", "Social", "Mới"];
const PRESET_SOURCES = ["Facebook", "Zalo", "Giới thiệu", "Khách vãng lai", "Google", "Giải đấu", "Khác"];

export default function CustomerModal({ open, onOpenChange, customer, allTags, allSources, onSaved }) {
  const isEdit = !!customer;
  const [form, setForm] = useState({
    name: "", phone: "", email: "", phone_secondary: "",
    address: "", tags: [], source: "", notes: "",
    payment_status: "", payment_amount: 0, group_id: "",
    join_date: "", end_date: "",
    gender: "", birthday: "", kv_code: "", region: "", ward: "",
    company: "", tax_code: "", customer_group: "",
  });
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [memberships, setMemberships] = useState([]); // [{member_id, group_id, group_name, join_date, end_date, months_paid, payment_status, is_active}]
  const [newGroupId, setNewGroupId] = useState("");
  const [savingMember, setSavingMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [newPayAmount, setNewPayAmount] = useState("");
  const [newPayDesc, setNewPayDesc] = useState("");
  const [newPayFrom, setNewPayFrom] = useState("");
  const [newPayTo, setNewPayTo] = useState("");
  const [addingPay, setAddingPay] = useState(false);

  useEffect(() => {
    if (open) {
      if (customer) {
        setForm({
          name: customer.name || "", phone: customer.phone || "",
          email: customer.email || "", phone_secondary: customer.phone_secondary || "",
          address: customer.address || "", tags: customer.tags || [],
          source: customer.source || "", notes: customer.notes || "",
          payment_status: customer.payment_status || "",
          payment_amount: customer.payment_amount || 0,
          group_id: customer.group_id || "",
          join_date: customer.join_date || "",
          end_date: customer.end_date || "",
          gender: customer.gender || "", birthday: customer.birthday || "",
          kv_code: customer.kv_code || "", region: customer.region || "",
          ward: customer.ward || "", company: customer.company || "",
          tax_code: customer.tax_code || "", customer_group: customer.customer_group || "",
        });
      } else {
        setForm({
          name: "", phone: "", email: "", phone_secondary: "",
          address: "", tags: [], source: "", notes: "",
          payment_status: "", payment_amount: 0, group_id: "",
          join_date: "", end_date: "",
          gender: "", birthday: "", kv_code: "", region: "", ward: "",
          company: "", tax_code: "", customer_group: "",
        });
      }
      setNewTag("");
      setPayments([]);
      setNewPayAmount("");
      setNewPayDesc("");
      setNewPayFrom("");
      setNewPayTo("");
    }
  }, [open, customer]);

  // Load groups + memberships (full detail)
  useEffect(() => {
    if (open) {
      api.get("/groups").then(r => setGroups(r.data.groups || [])).catch(() => {});
      if (customer?.id) {
        api.get(`/customers/${customer.id}`)
          .then(r => setMemberships(r.data.customer?.group_memberships || []))
          .catch(() => setMemberships([]));
      } else {
        setMemberships([]);
      }
      setNewGroupId("");
    }
  }, [open, customer]);

  // Load payment history
  const loadPayments = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const { data } = await api.get(`/customers/${customer.id}/payments`);
      setPayments(data.payments || []);
    } catch { /* */ }
  }, [customer]);

  useEffect(() => {
    if (open && isEdit) loadPayments();
  }, [open, isEdit, loadPayments]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const addTag = (tag) => { if (tag && !form.tags.includes(tag)) setField("tags", [...form.tags, tag]); setNewTag(""); };
  const removeTag = (tag) => setField("tags", form.tags.filter(t => t !== tag));

  const handleAddPayment = async () => {
    const amount = parseInt(newPayAmount);
    if (!amount || amount <= 0) { toast.error("Nhập số tiền hợp lệ"); return; }
    setAddingPay(true);
    try {
      const desc = newPayDesc || `Đóng tiền ${new Date().toLocaleDateString("vi-VN")}`;
      const { data } = await api.post(`/customers/${customer.id}/payments`, { amount, description: desc, from_date: newPayFrom, to_date: newPayTo });
      setPayments(prev => [data.payment, ...prev]);
      setForm(f => ({ ...f, payment_amount: data.total, payment_status: "paid" }));
      setNewPayAmount("");
      setNewPayDesc("");
      setNewPayFrom("");
      setNewPayTo("");
      toast.success(`Đã thêm ${formatVND(amount)}đ`);
    } catch { toast.error("Lỗi thêm thanh toán"); }
    finally { setAddingPay(false); }
  };

  const handleDeletePayment = async (payId) => {
    try {
      const { data } = await api.delete(`/customers/${customer.id}/payments/${payId}`);
      setPayments(prev => prev.filter(p => p.id !== payId));
      setForm(f => ({ ...f, payment_amount: data.total }));
      toast.success("Đã xóa");
    } catch { toast.error("Lỗi"); }
  };

  // --- Memberships handlers ---
  const handleMembershipChange = (memberId, field, value) => {
    setMemberships(prev => prev.map(m => m.member_id === memberId ? { ...m, [field]: value, _dirty: true } : m));
  };

  const saveMembershipField = async (m, field) => {
    if (!customer?.id) return;
    setSavingMember(m.member_id);
    try {
      const payload = { [field]: field === "months_paid" ? parseInt(m[field]) || 0 : m[field] };
      await api.put(`/customers/${customer.id}/memberships/${m.member_id}`, payload);
      setMemberships(prev => prev.map(x => x.member_id === m.member_id ? { ...x, _dirty: false } : x));
      toast.success("Đã cập nhật");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi cập nhật");
    } finally {
      setSavingMember(null);
    }
  };

  const handleDeleteMembership = async (memberId) => {
    if (!customer?.id) return;
    if (!window.confirm("Xoá khách khỏi hội này?")) return;
    try {
      await api.delete(`/customers/${customer.id}/memberships/${memberId}`);
      setMemberships(prev => prev.filter(m => m.member_id !== memberId));
      toast.success("Đã xoá khỏi hội");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleAddMembership = async () => {
    if (!newGroupId || !customer?.id) return;
    if (memberships.some(m => m.group_id === newGroupId)) {
      toast.error("Khách đã ở trong hội này");
      return;
    }
    try {
      const today = new Date().toISOString().slice(0, 10);
      // Post vào groups members endpoint
      const { data } = await api.post(`/groups/${newGroupId}/members`, {
        customer_id: customer.id,
        name: form.name || customer.name,
        phone: form.phone || customer.phone || "",
        join_date: today,
        end_date: "",
        months_paid: 0,
        payment_status: "unpaid",
      });
      const group = groups.find(g => g.id === newGroupId);
      setMemberships(prev => [...prev, {
        member_id: data.member?.id || data.id || "",
        group_id: newGroupId,
        group_name: group?.name || "",
        join_date: today,
        end_date: "",
        months_paid: 0,
        payment_status: "unpaid",
        status: "active",
        is_active: true,
      }]);
      setNewGroupId("");
      toast.success("Đã thêm vào hội");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi thêm hội");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vui lòng nhập tên khách hàng"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/customers/${customer.id}`, form);
        toast.success("Đã cập nhật khách hàng");
      } else {
        await api.post("/customers", form);
        toast.success("Đã thêm khách hàng mới");
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Có lỗi xảy ra"); }
    finally { setSaving(false); }
  };

  const availableTags = [...new Set([...PRESET_TAGS, ...(allTags || [])])].filter(t => !form.tags.includes(t));
  const availableSources = [...new Set([...PRESET_SOURCES, ...(allSources || [])])];
  const paymentTotal = payments.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading">
            {isEdit ? "Sửa khách hàng" : "Thêm khách hàng"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Họ tên *</label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)}
              className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-name-input" />
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">SĐT chính</label>
              <Input value={form.phone}
                onChange={(e) => setField("phone", e.target.value.replace(/\s/g, ""))}
                onKeyDown={blockWhitespaceKey}
                onBlur={(e) => setField("phone", normalizePhone(e.target.value))}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-phone-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email</label>
              <Input value={form.email} onChange={(e) => setField("email", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>

          {/* Phone secondary + Address */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">SĐT phụ</label>
              <Input value={form.phone_secondary}
                onChange={(e) => setField("phone_secondary", e.target.value.replace(/\s/g, ""))}
                onKeyDown={blockWhitespaceKey}
                onBlur={(e) => setField("phone_secondary", normalizePhone(e.target.value))}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Địa chỉ</label>
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>

          {/* Gender + Birthday */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Giới tính</label>
              <Select value={form.gender || "none"} onValueChange={(v) => setField("gender", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200">
                  <SelectValue placeholder="Chọn..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="none" className="text-slate-300">— Chưa chọn —</SelectItem>
                  <SelectItem value="Nam" className="text-slate-300">Nam</SelectItem>
                  <SelectItem value="Nữ" className="text-slate-300">Nữ</SelectItem>
                  <SelectItem value="Khác" className="text-slate-300">Khác</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày sinh (dd/mm/yyyy)</label>
              <Input value={form.birthday} onChange={(e) => setField("birthday", e.target.value)}
                placeholder="20/10/1992" className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>

          {/* Region + Ward */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Khu vực</label>
              <Input value={form.region} onChange={(e) => setField("region", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Phường/Xã</label>
              <Input value={form.ward} onChange={(e) => setField("ward", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>

          {/* Company + Tax + KV Code + Customer Group */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Công ty</label>
              <Input value={form.company} onChange={(e) => setField("company", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mã số thuế</label>
              <Input value={form.tax_code} onChange={(e) => setField("tax_code", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mã KH (KiotViet)</label>
              <Input value={form.kv_code} onChange={(e) => setField("kv_code", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Nhóm khách hàng</label>
              <Input value={form.customer_group} onChange={(e) => setField("customer_group", e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map(tag => (
                <Badge key={tag} className="bg-lime-500/20 text-lime-400 border-lime-500/30 text-[10px] px-2 py-0.5 border gap-1">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}><X className="w-2.5 h-2.5" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Select value="" onValueChange={(v) => { if (v) addTag(v); }}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 flex-1">
                  <SelectValue placeholder="Chọn tag..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {availableTags.map(t => (
                    <SelectItem key={t} value={t} className="text-slate-300">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Tag mới"
                  className="bg-[#0f172a] border-[#334155] text-slate-200 w-24" />
                <Button type="button" size="sm" variant="outline" onClick={() => addTag(newTag)}
                  className="border-[#334155] text-lime-400"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>

          {/* Source */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nguồn khách</label>
            <Select value={form.source || "none"} onValueChange={(v) => setField("source", v === "none" ? "" : v)}>
              <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-source-select">
                <SelectValue placeholder="Chọn nguồn..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1e293b] border-[#334155]">
                <SelectItem value="none" className="text-slate-300">— Chưa chọn —</SelectItem>
                {availableSources.map(s => (
                  <SelectItem key={s} value={s} className="text-slate-300">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Group (hội chính - backward compat) */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
              <Users className="w-3 h-3" /> Hội chính <span className="text-slate-600">(sẽ tự thêm vào danh sách bên dưới)</span>
            </label>
            <Select value={form.group_id || "none"} onValueChange={(v) => setField("group_id", v === "none" ? "" : v)}>
              <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-group-select">
                <SelectValue placeholder="Chưa có hội" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e293b] border-[#334155]">
                <SelectItem value="none" className="text-slate-300">— Chưa có hội —</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id} className="text-slate-300">{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Memberships table (edit mode only) */}
          {isEdit && (
            <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2" data-testid="memberships-panel">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1">
                <Users className="w-3 h-3 text-purple-400" /> Danh sách Hội ({memberships.length})
              </label>
              {memberships.length === 0 ? (
                <p className="text-[10px] text-slate-600 text-center py-2">Chưa thuộc hội nào</p>
              ) : (
                <div className="space-y-1.5">
                  {memberships.map((m) => (
                    <div key={m.member_id}
                      className={`border rounded-md p-2 ${m.is_active ? "bg-[#1e293b] border-purple-500/30" : "bg-[#1e293b]/50 border-slate-600/30"}`}
                      data-testid={`membership-row-${m.member_id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-bold ${m.is_active ? "text-purple-300" : "text-slate-500 line-through"}`}>
                            {m.group_name || "(Hội?)"}
                          </span>
                          <Badge className={`text-[9px] px-1.5 py-0 border ${
                            m.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : m.status === "expiring_soon" ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                              : "bg-slate-500/20 text-slate-400 border-slate-500/30"
                          }`}>
                            {m.status === "active" ? "Đang hoạt động" : m.status === "expiring_soon" ? "Sắp hết hạn" : "Hết hạn"}
                          </Badge>
                        </div>
                        <button type="button" onClick={() => handleDeleteMembership(m.member_id)}
                          className="text-slate-600 hover:text-red-400 transition-colors"
                          data-testid={`remove-membership-${m.member_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div>
                          <span className="text-[9px] text-slate-500 block mb-0.5">Ngày ĐK</span>
                          <DateInputVN value={m.join_date || ""}
                            onChange={(v) => { handleMembershipChange(m.member_id, "join_date", v); }}
                            onBlur={() => m._dirty && saveMembershipField(m, "join_date")}
                            className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs"
                            data-testid={`membership-join-${m.member_id}`} />
                        </div>
                        <div>
                          <span className="text-[9px] text-slate-500 block mb-0.5">Ngày hết hạn</span>
                          <DateInputVN value={m.end_date || ""}
                            onChange={(v) => { handleMembershipChange(m.member_id, "end_date", v); }}
                            onBlur={() => m._dirty && saveMembershipField(m, "end_date")}
                            className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs"
                            data-testid={`membership-end-${m.member_id}`} />
                        </div>
                        <div>
                          <span className="text-[9px] text-slate-500 block mb-0.5">Tháng đã đóng</span>
                          <Input type="number" min={0} value={m.months_paid || 0}
                            onChange={(e) => handleMembershipChange(m.member_id, "months_paid", e.target.value)}
                            onBlur={() => m._dirty && saveMembershipField(m, "months_paid")}
                            className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs"
                            data-testid={`membership-months-${m.member_id}`} />
                        </div>
                        <div>
                          <span className="text-[9px] text-slate-500 block mb-0.5">Thanh toán</span>
                          <Select value={m.payment_status || "unpaid"}
                            onValueChange={(v) => {
                              handleMembershipChange(m.member_id, "payment_status", v);
                              saveMembershipField({ ...m, payment_status: v, _dirty: true }, "payment_status");
                            }}>
                            <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-xs" data-testid={`membership-pay-${m.member_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1e293b] border-[#334155]">
                              <SelectItem value="paid" className="text-slate-300">Đã thanh toán</SelectItem>
                              <SelectItem value="unpaid" className="text-slate-300">Chưa TT</SelectItem>
                              <SelectItem value="overdue" className="text-slate-300">Quá hạn</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {savingMember === m.member_id && (
                        <p className="text-[9px] text-slate-500 mt-1">Đang lưu...</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Add to new group */}
              <div className="flex gap-2 pt-1" data-testid="add-membership-row">
                <Select value={newGroupId} onValueChange={setNewGroupId}>
                  <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs flex-1" data-testid="add-membership-select">
                    <SelectValue placeholder="Chọn hội để thêm..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-[#334155]">
                    {groups.filter(g => !memberships.some(m => m.group_id === g.id)).map(g => (
                      <SelectItem key={g.id} value={g.id} className="text-slate-300">{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" size="sm" onClick={handleAddMembership}
                  disabled={!newGroupId}
                  className="bg-purple-500 text-white h-8 text-xs font-semibold px-3 disabled:opacity-40" data-testid="add-membership-btn">
                  <Plus className="w-3 h-3 mr-1" /> Thêm
                </Button>
              </div>
            </div>
          )}

          {/* Join date / End date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày đăng ký</label>
              <DateInputVN value={form.join_date || ""} onChange={(v) => setField("join_date", v)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-join-date" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày hết hạn</label>
              <DateInputVN value={form.end_date || ""} onChange={(v) => setField("end_date", v)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-end-date" />
            </div>
          </div>

          {/* Payment status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Trạng thái thanh toán</label>
              <Select value={form.payment_status || "none"} onValueChange={(v) => setField("payment_status", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="customer-payment-status">
                  <SelectValue placeholder="Chưa có" />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="none" className="text-slate-300">Chưa có</SelectItem>
                  <SelectItem value="paid" className="text-slate-300">Đã thanh toán</SelectItem>
                  <SelectItem value="unpaid" className="text-slate-300">Chưa thanh toán</SelectItem>
                  <SelectItem value="overdue" className="text-slate-300">Quá hạn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tổng đã đóng</label>
              <div className="bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-lime-400 font-bold text-sm">
                {formatVND(paymentTotal || form.payment_amount || 0)}đ
              </div>
            </div>
          </div>

          {/* Payment History (edit only) */}
          {isEdit && (
            <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-lime-400" /> Lịch sử đóng tiền
              </label>
              {/* Add payment */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input type="number" value={newPayAmount} onChange={(e) => setNewPayAmount(e.target.value)}
                    placeholder="Số tiền" className="bg-[#1e293b] border-[#334155] text-slate-200 h-8 text-xs flex-1" data-testid="new-payment-amount" />
                  <Input value={newPayDesc} onChange={(e) => setNewPayDesc(e.target.value)}
                    placeholder="Ghi chú (VD: Tháng 4)" className="bg-[#1e293b] border-[#334155] text-slate-200 h-8 text-xs flex-1" data-testid="new-payment-desc" />
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-[10px] text-slate-600 shrink-0">Từ</span>
                  <DateInputVN value={newPayFrom} onChange={setNewPayFrom}
                    className="bg-[#1e293b] border-[#334155] text-slate-200 h-8 text-xs flex-1" data-testid="new-payment-from" />
                  <span className="text-[10px] text-slate-600 shrink-0">đến</span>
                  <DateInputVN value={newPayTo} onChange={setNewPayTo}
                    className="bg-[#1e293b] border-[#334155] text-slate-200 h-8 text-xs flex-1" data-testid="new-payment-to" />
                  <Button type="button" size="sm" onClick={handleAddPayment} disabled={addingPay}
                    className="bg-lime-500 text-[#0f172a] h-8 text-xs font-semibold px-3" data-testid="add-payment-btn">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {/* History list */}
              <div className="max-h-[120px] overflow-y-auto space-y-1">
                {payments.length === 0 ? (
                  <p className="text-[10px] text-slate-600 text-center py-2">Chưa có lịch sử</p>
                ) : payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-1 px-2 rounded bg-[#1e293b]/50 text-xs group">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-lime-400 font-bold shrink-0">+{formatVND(p.amount)}đ</span>
                      <span className="text-slate-500 truncate">{p.description}</span>
                      {(p.from_date || p.to_date) && (
                        <span className="text-[10px] text-slate-600 shrink-0">({p.from_date || "?"} → {p.to_date || "?"})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] text-slate-600">{p.created_at ? new Date(p.created_at).toLocaleDateString("vi-VN") : ""}</span>
                      <button type="button" onClick={() => handleDeletePayment(p.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-red-400 transition-opacity">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Ghi chú</label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
              placeholder="Ghi chú về khách hàng..." className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[60px]" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
            <Button type="submit" disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold" data-testid="customer-save-btn">
              {saving ? "Đang lưu..." : (isEdit ? "Cập nhật" : "Thêm mới")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
